import React, { useState, useEffect } from "react";
import { Box, Typography, Grid, TextField, Button, Paper } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const numericRequired = (value) => {
  if (value === "" || value === null || value === undefined) return "Requerido";
  const n = Number(value);
  if (Number.isNaN(n) || n <= 0) return "Debe ser mayor a 0";
  return "";
};

const onlyNumbers = (value) => value.replace(/\D/g, "");

export default function StepPackages({
  type,
  packages,
  setPackages,
  onNext,
  onBack,
  onPackageCountCommit,
  selectedProducts = [],
  multipackComponentsBySku = {},
}) {
  const [count, setCount] = useState(
    packages?.length ? String(packages.length) : ""
  );
  const [countError, setCountError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const hasSimpleProducts = selectedProducts.some((product) => {
    const components = multipackComponentsBySku[product.id];
    return (
      !product.is_multipack ||
      !Array.isArray(components) ||
      components.length === 0
    );
  });

  const totalBultosExpected = Array.isArray(selectedProducts)
    ? selectedProducts.reduce((acc, product) => {
      const components = multipackComponentsBySku[product.id];
      if (
        product.is_multipack &&
        Array.isArray(components) &&
        components.length > 0
      ) {
        return acc + (Number(product.amountEntered) || 0) * components.length;
      }
      return acc;
    }, 0) + (hasSimpleProducts ? 1 : 0)
    : 0;

  const maxBultos = Array.isArray(selectedProducts)
    ? selectedProducts.reduce((acc, product) => {
      const components = multipackComponentsBySku[product.id];
      const multiplier =
        product.is_multipack &&
          Array.isArray(components) &&
          components.length > 0
          ? components.length
          : 1;
      return acc + (Number(product.amountEntered) || 0) * multiplier;
    }, 0)
    : 0;

  const isSingleUnitShipment =
    totalBultosExpected === 1 && maxBultos === 1 && type === "cliente";
  const singleProduct =
    isSingleUnitShipment && selectedProducts.length > 0
      ? selectedProducts[0]
      : null;

  useEffect(() => {
    if (packages && packages.length && !count) {
      setCount(String(packages.length));
    }
  }, [packages, count]);

  useEffect(() => {
    if (!isSingleUnitShipment || !singleProduct) return;
    if (!packages || packages.length === 0) {
      const width = singleProduct.width ?? "";
      const height = singleProduct.height ?? "";
      const depth = singleProduct.deep ?? "";
      const total_weight = singleProduct.weight ?? "";

      setPackages([
        {
          index: 1,
          width: width !== null && width !== undefined ? String(width) : "",
          height: height !== null && height !== undefined ? String(height) : "",
          depth: depth !== null && depth !== undefined ? String(depth) : "",
          declared_value: "",
          total_weight:
            total_weight !== null && total_weight !== undefined
              ? String(total_weight)
              : "",
        },
      ]);
      setCount("1");
      setCountError("");
    }
  }, [isSingleUnitShipment, singleProduct]);

  useEffect(() => {
    if (totalBultosExpected < 1 || isSingleUnitShipment) return;

    const currentLength = Array.isArray(packages) ? packages.length : 0;
    const countNum = parseInt(count, 10);

    if (
      currentLength === 0 ||
      (currentLength !== totalBultosExpected &&
        (!count || countNum !== currentLength))
    ) {
      setCount(String(totalBultosExpected));
      setCountError("");
      const defaultVal = type === "cliente" ? "1" : "";
      setPackages(
        Array.from({ length: totalBultosExpected }, (_, i) => ({
          index: i + 1,
          width: defaultVal,
          height: defaultVal,
          depth: defaultVal,
          declared_value: defaultVal,
          total_weight: defaultVal,
        }))
      );
    }
  }, [totalBultosExpected, isSingleUnitShipment]);

  const handleCountChange = (e) => {
    if (isSingleUnitShipment) {
      setCount("1");
      return;
    }

    const value = onlyNumbers(e.target.value);
    setCount(value);

    if (value === "") {
      setCountError("");
      setPackages([]);
      return;
    }

    const n = parseInt(value, 10);
    if (Number.isNaN(n) || n < 1) {
      setCountError("Ingrese una cantidad válida (mínimo 1)");
      return;
    }

    if (n < totalBultosExpected) {
      setCountError(`Mínimo requerido: ${totalBultosExpected} bultos.`);
      return;
    }

    if (n > maxBultos) {
      setCountError(`Máximo permitido: ${maxBultos} bultos.`);
      return;
    }

    setCountError("");
    const defaultVal = type === "cliente" ? "1" : "";
    setPackages((prev) => {
      const current = Array.isArray(prev) ? [...prev] : [];
      if (n > current.length) {
        for (let i = current.length; i < n; i++) {
          current.push({
            index: i + 1,
            width: defaultVal,
            height: defaultVal,
            depth: defaultVal,
            declared_value: defaultVal,
            total_weight: defaultVal,
          });
        }
      } else {
        current.length = n;
      }
      return current.map((pkg, idx) => ({ ...pkg, index: idx + 1 }));
    });
  };

  const handleFieldChange = (index, field, value) => {
    setPackages((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    setFieldErrors((prev) => {
      const copy = { ...prev };
      copy[index] = { ...(copy[index] || {}), [field]: "" };
      return copy;
    });
  };

  const validatePackages = () => {
    const n = parseInt(count, 10);
    if (!count || Number.isNaN(n) || n < totalBultosExpected || n > maxBultos) {
      setCountError(
        `Ingrese una cantidad entre ${totalBultosExpected} y ${maxBultos}`
      );
      return false;
    }

    // Para "Cliente retira" solo se valida la cantidad, no las dimensiones
    if (type === "cliente" || type === "redespacho" || type === "transporte_manual") return true;

    const errors = {};
    let hasErrors = false;
    (packages || []).forEach((pkg, idx) => {
      const e = {};
      ["width", "height", "depth", "declared_value", "total_weight"].forEach(
        (field) => {
          const msg = numericRequired(pkg?.[field]);
          if (msg) {
            e[field] = msg;
            hasErrors = true;
          }
        }
      );
      if (Object.keys(e).length) errors[idx] = e;
    });
    setFieldErrors(errors);
    return !hasErrors;
  };

  const canContinue = () => {
    const n = parseInt(count, 10);
    if (!count || Number.isNaN(n) || n < totalBultosExpected || n > maxBultos)
      return false;

    // Para "Cliente retira" solo se necesita la cantidad de bultos
    if (type === "cliente" || type === "redespacho" || type === "transporte_manual") return true;

    if (!packages || packages.length !== n) return false;

    return packages.every((pkg) =>
      ["width", "height", "depth", "declared_value", "total_weight"].every(
        (f) => numericRequired(pkg?.[f]) === ""
      )
    );
  };

  return (
    <Box>
      <Typography variant="h6" mb={3} fontWeight="bold">
        Ingrese la Cantidad de Bultos
      </Typography>
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Cantidad de bultos"
            value={count}
            onChange={handleCountChange}
            size="small"
            error={!!countError}
            disabled={isSingleUnitShipment}
            helperText={countError}
          />
        </Grid>
      </Grid>

      {type !== "cliente" && type !== "redespacho" && type !== "transporte_manual" &&
        packages.map((pkg, idx) => (
          <Paper
            key={idx}
            variant="outlined"
            sx={{ p: 2, mb: 2, borderRadius: 2 }}
          >
            <Typography variant="subtitle2" fontWeight="bold" mb={2}>
              Bulto {idx + 1}
            </Typography>
            <Grid container spacing={2}>
              {[
                { key: "width", label: "Ancho (cm) *" },
                { key: "height", label: "Alto (cm) *" },
                { key: "depth", label: "Prof. (cm) *" },
                { key: "declared_value", label: "Valor (ARS) *" },
                { key: "total_weight", label: "Peso (gr) *" },
              ].map(({ key, label }) => (
                <Grid item xs={12} sm={6} md={2.4} key={key}>
                  <TextField
                    fullWidth
                    label={label}
                    size="small"
                    value={pkg[key]}
                    error={!!fieldErrors[idx]?.[key]}
                    onChange={(e) =>
                      handleFieldChange(idx, key, onlyNumbers(e.target.value))
                    }
                    disabled={isSingleUnitShipment && key !== "declared_value"}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
        ))}

      <Box mt={4} display="flex" justifyContent="space-between">
        <Button
          variant="outlined"
          onClick={onBack}
          startIcon={<ArrowBackIcon />}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            borderColor: "#cbd5e1",
            color: "#475569",
            "&:hover": { borderColor: "#94a3b8", backgroundColor: "#f8fafc" },
          }}
        >
          Volver
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            if (!validatePackages()) return;
            const n = parseInt(count, 10);
            if (typeof onPackageCountCommit === "function" && !Number.isNaN(n)) {
              onPackageCountCommit(n);
            }
            onNext();
          }}
          disabled={!canContinue()}
          endIcon={<ArrowForwardIcon />}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            px: 3,
          }}
        >
          Siguiente
        </Button>
      </Box>
    </Box>
  );
}
